import { useCallback, useEffect, useMemo } from 'react';

import { useRouter } from 'expo-router';
import { useIntl } from 'react-intl';
import { StyleSheet, Text, View } from 'react-native';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { CardModal } from 'components/card-modal';
import LoyaltyPointsMedal from 'components/icons/loyalty-points-medal';
import YouRuleLogo from 'components/you-rule-logo';
import { useLoyaltyContext } from 'state/loyalty';
import { primitive } from 'styles/constants/primitives';

const ChallengeCompleted = () => {
  const router = useRouter();
  const { formatMessage } = useIntl();
  const { challengeCompletedModalData } = useLoyaltyContext();
  const { monthlyChallengePoints = 0, profileChallengePoints = 0 } = challengeCompletedModalData;

  const handleClose = useCallback(() => {
    router.dismiss();
  }, [router]);

  const ctaButtonLabel = formatMessage({ id: 'gotIt', defaultMessage: 'Got It' });

  const totalPointsEarned = monthlyChallengePoints + profileChallengePoints;

  const descriptionMessage = useMemo(() => {
    const hasMonthly = monthlyChallengePoints > 0;
    const hasProfile = profileChallengePoints > 0;

    if (hasMonthly && hasProfile) {
      return formatMessage({
        id: 'youCompletedYourMonthlyAndProfileChallenges',
        defaultMessage: `You've successfully completed your Profile\nand Monthly Challenge.`,
      });
    }

    if (hasMonthly) {
      return formatMessage({
        id: 'youCompletedYourMonthlyChallenge',
        defaultMessage: `You've successfully completed your\nMonthly Challenge.`,
      });
    }

    return formatMessage({
      id: 'youCompletedYourProfileChallenge',
      defaultMessage: `You've successfully completed your\nProfile Challenge.`,
    });
  }, [monthlyChallengePoints, profileChallengePoints, formatMessage]);

  useEffect(() => {
    if (!monthlyChallengePoints && !profileChallengePoints) {
      handleClose();
    }
  }, [handleClose, monthlyChallengePoints, profileChallengePoints]);

  return (
    <CardModal
      alwaysShowHeader
      webAnimate
      standardPadding
      customHeaderLeft={<YouRuleLogo />}
      headerFlexStartStyleOverride={{ alignItems: 'center' }}
      headerFlexEndStyleOverride={{ flex: 0 }}
      wrappedChildrenStyle={{ backgroundColor: primitive.bk.$mayoEggWhite }}
      onDismiss={handleClose}
      footer={
        <ActionButton
          fullWidth
          accessibilityRole="link"
          onPress={handleClose}
          variant={ActionButtonVariants.OUTLINE}
          accessibilityLabel={ctaButtonLabel}
        >
          {ctaButtonLabel}
        </ActionButton>
      }
    >
      <View style={styles.container}>
        <LoyaltyPointsMedal points={totalPointsEarned} size={180} />
        <Text style={styles.title}>
          {formatMessage({ id: 'youDidIt', defaultMessage: 'You Did It!' })}
        </Text>
        <Text style={styles.description}>{descriptionMessage}</Text>
        <Text style={styles.caption}>
          {formatMessage(
            {
              id: 'crownsAllYours',
              defaultMessage: `That's {points} Crowns — all yours!`,
            },
            {
              points: totalPointsEarned,
            }
          )}
        </Text>
      </View>
    </CardModal>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: primitive.spacing.$11,
  },
  title: {
    fontFamily: primitive.fontFamily.$headers,
    fontSize: primitive.fontSizes.xl,
    marginTop: primitive.spacing.$7,
  },
  description: {
    fontSize: primitive.fontSizes.md,
    fontFamily: primitive.fontFamily.$body,
    marginTop: primitive.spacing.$2,
    lineHeight: primitive.fontSizes.md * 1.4,
    textAlign: 'center',
  },
  caption: {
    fontSize: primitive.fontSizes.sm,
    fontFamily: primitive.fontFamily.$body,
    marginTop: primitive.spacing.$9,
    textAlign: 'center',
  },
});

export default ChallengeCompleted;
